import { Component, OnInit, Input, ChangeDetectorRef } from "@angular/core";
import { LaunchWallboardService } from "../launch-wallboard.service";
import { WallboardGridster, WallboardSlideGridster, ModuleType, CustomModuleTypes, TypeStateApiModel, MetricEventObj } from "src/app/wallboards/wallboards.model";
import { UserProfileDTO } from "../../user/user-admin.model";
import { WallboardsService } from "src/app/wallboards/wallboards.service";
import { CompactType, DisplayGrid, GridType, GridsterConfig } from "angular-gridster2";

@Component({
    selector: "launch-wallboard-preview",
    templateUrl: "./launch-wallboard-preview.component.html",
    styleUrls: ["./launch-wallboard-preview.component.scss"],
})
export class LaunchWallboardPreviewComponent implements OnInit {
    @Input() public wallboard: WallboardGridster;

    public spotlightLoadedProfile: UserProfileDTO;
    public spotlightLoadedLoginId = -1;
    public selectedMetricInfo: MetricEventObj[] = [];
    public racingBackgroundImage: string = null;
    public gridsterOptions: GridsterConfig;

    get FirstSlide(): WallboardSlideGridster {
        return this.wallboard.Slides[0] ?? null;
    }
    get FirstSlideType(): ModuleType {
        let moduleDetails = this.FirstSlide?.WallboardModuleDetails ?? [];
        if (moduleDetails.length == 0)
            return null;
        if (CustomModuleTypes.includes(moduleDetails[0]?.WallboardModuleID))
            return 0;

        return moduleDetails[0].WallboardModuleID;
    }
    get TypeState(): TypeStateApiModel {
        return this.FirstSlide?.TypeState ?? null;
    }

    constructor(
        public launchWallboardService: LaunchWallboardService,
        private wallboardsService: WallboardsService,
        private cdr: ChangeDetectorRef,
    ) { }

    public async ngOnInit() {
        if (!this.FirstSlide)
            return;

        if (this.FirstSlideType == ModuleType.Custom) {
            this.loadCustomPreview();
            return;
        }

        if (!this.TypeState && this.FirstSlide.WallboardModuleDetails.length > 0)
            this.FirstSlide.TypeState = await this.launchWallboardService.loadTypeState(this.FirstSlideType, this.FirstSlide.WallboardModuleDetails[0].WallboardModuleKey, this.FirstSlide.CreatorID);

        switch (this.FirstSlideType) {
            case ModuleType.Racing:
                this.racingBackgroundImage = this.wallboardsService.racingThemePreviewImageFilename(this.TypeState?.Racing_Setup.Theme);
                break;
            case ModuleType.Summary: {
                let slideControls = await this.wallboardsService.loadSlideControls(this.FirstSlide.WallboardModuleDetails[0].WallboardModuleKey, this.FirstSlide.CreatorID, 10, false);
                this.selectedMetricInfo = slideControls.Selected_Metric_Info;
                break;
            }
            case ModuleType.Image:
                this.FirstSlide.BackgroundImageID = this.TypeState.Image_Settings.UploadedImageID;
                break;
            case ModuleType.Spotlight: {
                let spotlightSetupObj = this.TypeState?.Spotlight_Setup;
                if (spotlightSetupObj?.UseLogin && spotlightSetupObj?.LoginID > 0 && spotlightSetupObj?.LoginID != this.spotlightLoadedLoginId) {
                    try {
                        const userProfile = await this.wallboardsService.loadSpotlightLoginInfo(spotlightSetupObj.LoginID);
                        this.spotlightLoadedLoginId = spotlightSetupObj.LoginID;
                        this.spotlightLoadedProfile = userProfile;
                    }
                    catch (error) {
                        console.log(error);
                    }
                }
                break;
            }
            default:
                break;
        }
    }

    /**
     * Loads the custom preview for the currently selected wallboard's first slide.
     */
    public async loadCustomPreview(): Promise<void> {
        this.gridsterOptions = {
            gridType: GridType.Fit,
            minCols: 40,
            maxCols: 40,
            minRows: 40,
            maxRows: 40,
            compactType: CompactType.None,
            displayGrid: DisplayGrid.None,
            margin: 8,
            disableScrollHorizontal: true,
            disableScrollVertical: true,
            resizable: {
                enabled: false,
            },
            draggable: {
                enabled: false,
            },
            mobileBreakpoint: 1,
        };

        for (let i = 0; i < this.FirstSlide.WallboardModuleDetails.length; i++) {
            const oldDetail = this.FirstSlide.WallboardModuleDetails[i];

            const newDetail = {
                WallboardModuleDetailsID: oldDetail.WallboardModuleDetailsID,
                CompanyID: oldDetail.CompanyID,
                WallboardID: oldDetail.WallboardID,
                WallboardSlideID: oldDetail.WallboardSlideID,
                PositionTop: Math.min(oldDetail.PositionTop, 32),
                PositionLeft: Math.min(oldDetail.PositionLeft, 32),
                Height: Math.max(oldDetail.Height, 8),
                Width: Math.max(oldDetail.Width, 8),
                WallboardModuleID: oldDetail.WallboardModuleID,
                WallboardModuleKey: oldDetail.WallboardModuleKey,
                Active: oldDetail.Active,

                x: oldDetail.PositionLeft,
                y: oldDetail.PositionTop,
                rows: Math.max(oldDetail.Height, 8),
                cols: Math.max(oldDetail.Width, 8),
                minItemRows: 8,
                minItemCols: 8,
                placeholder: false,

                State: null
            };

            // Starting/minimum module sizes
            switch (oldDetail.WallboardModuleID) {
                case ModuleType.Gauge:
                case ModuleType.ImageModule:
                    newDetail.minItemRows = 15;
                    newDetail.minItemCols = 10;
                    break;
                case ModuleType.Thermometer:
                    newDetail.minItemRows = 30;
                    newDetail.minItemCols = 10;
                    break;
                case ModuleType.SummaryModule:
                case ModuleType.Achievement:
                    newDetail.minItemRows = 15;
                    newDetail.minItemCols = 30;
                    break;
                case ModuleType.Text:
                    newDetail.minItemRows = 10;
                    newDetail.minItemCols = 10;
                    break;
                case ModuleType.Top:
                    newDetail.minItemRows = 15;
                    newDetail.minItemCols = 20;
                    break;
                case ModuleType.AgentCounter:
                    newDetail.minItemRows = 15;
                    newDetail.minItemCols = 15;
                    break;
                // KPI is the smallest at 8x8, which is the default set above
                default:
                    break;
            }

            // This section ensures that the module size and position values are renderable
            newDetail.Height = Math.min(Math.max(newDetail.minItemRows, oldDetail.Height), 40);
            newDetail.rows = newDetail.Height;

            newDetail.PositionTop = Math.max(Math.min(40 - newDetail.Height, oldDetail.PositionTop), 0);
            newDetail.y = newDetail.PositionTop;

            newDetail.Width = Math.min(Math.max(newDetail.minItemCols, oldDetail.Width), 40);
            newDetail.cols = newDetail.Width;

            newDetail.PositionLeft = Math.max(Math.min(40 - newDetail.Width, oldDetail.PositionLeft), 0);
            newDetail.x = newDetail.PositionLeft;

            this.FirstSlide.WallboardModuleDetails[i] = newDetail;
        }

        if (!this.FirstSlide.WallboardModuleDetails[0].State) {
            const typeList = this.FirstSlide.WallboardModuleDetails.map((o) => o.WallboardModuleID);
            const keyList = this.FirstSlide.WallboardModuleDetails.map((o) => o.WallboardModuleKey);
            try {
                const moduleStates = await this.wallboardsService.loadModuleStates(typeList, keyList, this.wallboard.CreatorLoginID);
                for (let i = 0; i < this.FirstSlide.WallboardModuleDetails.length; i++) {
                    this.FirstSlide.WallboardModuleDetails[i].State = moduleStates[i];
                }
            }
            catch (error) {
                console.log(error);
            }
        }

        // This is necessary for the charts to render properly
        this.cdr.detectChanges();

        if (this.FirstSlide.WallboardModuleDetails.find(x => x.WallboardModuleID == 16) != null)
            this.loadCanvas();
    }

    /**
     * Returns the correct value for the background field for the main section of the currently selected slide.
     * */
    public mainSectionBackgroundUrl(noSlides: boolean = false): string {
        let mainSection = document.getElementById('mainSection');
        let centerBoxHeight = mainSection ? mainSection.clientHeight : 0;
        let centerBoxWidth = mainSection ? mainSection.clientWidth : 0;

        let url = `../Admin/ImageResizer.aspx?imgid=${!noSlides ? this.FirstSlide.BackgroundImageID : "-51"}&w=${centerBoxWidth}&h=${centerBoxHeight}`;
        if (this.FirstSlideType == ModuleType.Racing && this.racingBackgroundImage)
            url = `../App_Themes/Standard/Images/Wallboards/${this.racingBackgroundImage}`;
        else if (this.FirstSlideType == ModuleType.Video && this.TypeState?.Video_Settings?.ThumbImg)
            url = this.TypeState?.Video_Settings?.ThumbImg;

        return `url(${url})`;
    }

    /**
     * Returns the text for the bio for the spotlight slide preview.
     * @param isBio
     */
    public getBioText(isBio: boolean): string {
        let spotlightSetupObj = this.TypeState?.Spotlight_Setup;
        if (!spotlightSetupObj)
            return;

        // Caps the profile Bio and the manual bio at 500 characters then appends ... if there is anything cut off.
        if (isBio) {
            if (this.spotlightLoadedProfile != null && this.spotlightLoadedLoginId == spotlightSetupObj.LoginID) {
                if (this.spotlightLoadedProfile.Bio.length > 499)
                    return this.spotlightLoadedProfile.Bio.substring(0, 496) + "...";
                else
                    return this.spotlightLoadedProfile.Bio;
            }
            else
                return "";
        }
        else if (spotlightSetupObj.BioString.length > 499)
            return spotlightSetupObj.BioString.substring(0, 496) + "...";
        else
            return spotlightSetupObj.BioString;
    }

    /**
     * Sets up the charts for any agent counter modules on the currently selected wallboard's first slide.
     */
    public loadCanvas() {
        let counterModules = this.FirstSlide?.WallboardModuleDetails.filter(o => o.WallboardModuleID == ModuleType.AgentCounter);

        for (let module of counterModules) {
            if (module.State.counterObj.chartOptions)
                module.State.counterObj.chartOptions.destroy();

            module.State.counterObj.chartOptions = new Chart(document.getElementById(`chart${module.WallboardModuleKey}`) as any, {
                type: `doughnut`,
                data: {
                    datasets: [
                        {
                            data: [1, 1, 1, 1, 1],
                            backgroundColor: [
                                `rgba(61, 150, 73, 1)`, // Available
                                `rgba(124, 82, 191, 1)`, // Inbound
                                `rgba(228, 179, 33, 1)`, // Outbound
                                `rgba(241, 102, 34, 1)`, // ACW
                                `rgba(189, 32, 38, 1)` // Unavailable
                            ],
                            borderColor: `transparent`,
                            borderWidth: 5
                        }
                    ],
                    labels: [
                        `Available`,
                        `Inbound`,
                        `Outbound`,
                        `ACW`,
                        `Unavailable`
                    ]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    animation: {
                        duration: 0
                    },
                    plugins: {
                        datalabels: {
                            anchor: (k) => `center`,
                            display: true,
                            backgroundColor: (context) => null,
                            borderRadius: (k) => 0,
                            borderWidth: (k) => 0,
                            color: 'white',
                            font: (context) => {
                                const width = context.chart.width - 64;
                                const height = context.chart.height - 64;

                                let size;
                                if (width > height)
                                    size = Math.round(height / 8);
                                else
                                    size = Math.round(width / 8);

                                const minSize = 14;

                                return {
                                    size: size < minSize ? minSize : size,
                                    weight: `bold`
                                };
                            },
                            formatter: (value, context) => value
                        },
                        legend: { display: false },
                        tooltip: { enabled: false },
                    },
                    layout: {
                        padding: {
                            left: 0,
                            right: 0,
                            top: 0,
                            bottom: 0
                        }
                    }
                }
            });
        }
    }
}
